import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, makeStyles, Typography } from '@material-ui/core';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  FiberManualRecord as FiberManualRecordIcon
} from '@material-ui/icons';
import { isNil } from 'lodash';
import InlineTextPopover from 'components/@common/InlineTextPopover';
import LwtTable from 'components/@common/Table/LwtTable';
import { PrmStudentCurriculumLessonReportResult } from 'types/schema-generated-types';
import { isPrmSetACurriculum } from '../../common/curriculum-util';
import useCommonStyles from '../../common/styles';

const Title: any = ({ text }: { text?: any }) =>
  text !== 0 && <Typography style={{ fontSize: 13 }}>{text}</Typography>;

const Label = ({ text }: any) => {
  return (
    <Box display="flex">
      <Typography style={{ fontSize: 12, fontWeight: 600 }}>{text}</Typography>
    </Box>
  );
};

type Keys = {
  id?: string | number;
  lessonNumber?: string | number;
  lessonSkill?: string | number;
  metSkill?: any;
  averageTime: any;
  usedSupport?: string;
};

const getColumns = () => {
  return [
    {
      id: 'lessonNumber',
      label: 'LESSON NUMBER',
      sortKey: 'lessonNumber',
      getValue(item: Keys) {
        return (
          <div key={`${item.id}_col_1`}>
            <Title text={item.lessonNumber} />
          </div>
        );
      },
      sortable: true
    },
    {
      id: 'lessonSkill',
      label: 'LESSON SKILL',
      sortKey: 'lessonSkill',
      getValue(item: Keys) {
        return (
          <div key={`${item.id}_col_1`}>
            <Title text={item.lessonSkill} />
          </div>
        );
      },
      sortable: true
    },
    {
      id: 'metSkill',
      label: <Label text="LESSON STATUS" />,
      sortKey: 'metSkill',
      getValue(item: Keys) {
        return !isNil(item.metSkill) ? (
          item.metSkill ? (
            <Box display="flex" key={`${item.id}_col_1`}>
              <Title text="Skill Met" />
              <CheckCircleIcon
                fontSize="small"
                style={{ color: '#51A486', fontSize: 14, alignSelf: 'center', marginLeft: 4 }}
              />
            </Box>
          ) : (
            <Box display="flex" key={`${item.id}_col_1`}>
              <Title text="Skill Not Met" />
              <CancelIcon
                fontSize="small"
                color="error"
                style={{ fontSize: 14, alignSelf: 'center', marginLeft: 4 }}
              />
            </Box>
          )
        ) : (
          '--'
        );
      }
    },
    {
      id: 'usedSupport',
      label: <Label text="ADDITIONAL SUPPORT" />,
      sortKey: 'usedSupport',
      getValue(item: any) {
        return !isNil(item.usedSupport) ? (
          <Box display="flex" key={`${item.id}_col_1`}>
            <Title text={item.usedSupport ? 'Yes' : 'No'} />
            {item.usedSupport && (
              <FiberManualRecordIcon
                fontSize="small"
                style={{ fontSize: 14, alignSelf: 'center', color: '#FFC20E', marginLeft: 4 }}
              />
            )}
          </Box>
        ) : (
          '--'
        );
      }
    },
    {
      id: 'averageTime',
      label: <Label text="TIME PER LESSON" />,
      sortKey: 'averageTime',
      getValue(item: Keys) {
        return (
          <div key={`${item.id}_col_1`}>
            {item.averageTime !== '0' ? <Title text={`${item.averageTime} minutes`} /> : '--'}
          </div>
        );
      }
    }
  ];
};

const getData = (studentLessonReport: PrmStudentCurriculumLessonReportResult): any[] => {
  const result = studentLessonReport.lessonDetail?.lessons.map(l => ({
    lessonNumber: l.lesson?.lessonNumber,
    lessonSkill: l.lesson?.primarySkillPhonics || l.lesson?.primarySkillWordStudy,
    usedSupport: l?.usedSupport,
    metSkill: l?.metSkill,
    averageTime: `${Math.round(l?.timeSpentSeconds! / 60)}`
  }));
  return result ?? [];
};

type Props = {
  studentLessonReport: PrmStudentCurriculumLessonReportResult;
};

const SkillProgressTable: React.FC<Props> = ({ studentLessonReport }) => {
  const commonClasses = useCommonStyles();
  const classes = useStyles();
  const { t } = useTranslation('ReportsHelpCommon');

  const titleText = 'Lesson Results';

  return (
    <Box className={commonClasses.container}>
      <Box display="flex">
        {isPrmSetACurriculum(studentLessonReport?.curriculumDetail?.productCurriculum) ? (
          <InlineTextPopover
            PopoverContentProps={{ description: t('prmSetAReporting') }}
            className={classes.title}
          >
            {titleText}
          </InlineTextPopover>
        ) : (
          <Typography className={classes.title}>{titleText}</Typography>
        )}
      </Box>
      <LwtTable
        data={getData(studentLessonReport)}
        columns={getColumns()}
        classes={{ headRow: () => classes.title }}
      />
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  title: { fontWeight: 450, fontSize: 24, paddingBottom: 15 },
  tableTitle: { fontSize: 12 }
}));

export default SkillProgressTable;
